import React, { useMemo } from 'react';
import styled from 'styled-components';
import {
  Contact,
  Dropdown,
  Menu,
  MenuItem,
  Avatar,
  AvatarGroup,
  Text,
} from 'components';
import { SecondaryDescriptionText } from '@brokerbay/design-components';

export interface MultiContactEntryProps {
  contacts: {
    id: string;
    firstName: string;
    lastName?: string;
    avatarFilestackId?: string;
    orgName: string;
  }[];
}

/** No longer being used for showings calendar but can be reused if
 * mutiple contacts fits your use case
 */
const MultiContactEntry = (props: MultiContactEntryProps) => {
  const { contacts } = props;

  const namesList = useMemo(
    () =>
      contacts
        .map(
          ({ firstName, lastName }) =>
            `${firstName}${lastName ? ` ${lastName}` : ''}`,
        )
        .join(', '),
    [contacts],
  );

  const avatarCountInitials = useMemo(() => {
    if (contacts.length < 10) {
      // 1-digit count can fit in first initial
      return { firstName: contacts.length.toString(10) };
    }
    if (contacts.length < 100) {
      // 2-digit count can fit in first and last initial
      const [firstName, lastName] = contacts.length.toString(10);
      return { firstName, lastName };
    }
    // 3+-digit count cannot fit in avatar component
    return { firstName: '…' };
  }, [contacts]);

  let ContactsSection: React.ReactNode;

  if (contacts.length === 1) {
    ContactsSection = <Contact contact={contacts[0]} underlineOnHover />;
  } else {
    const dropdownMenu = (
      <StyledMenu>
        {contacts.map(contact => (
          <MenuItem key={contact.id}>
            <Contact
              avatarSize={32}
              contact={contact}
              secondaryText={
                <StyledAddressName ellipsis>
                  {contact.orgName}
                </StyledAddressName>
              }
            />
          </MenuItem>
        ))}
      </StyledMenu>
    );

    ContactsSection = (
      <Dropdown
        dropdownRender={() => dropdownMenu}
        getPopupContainer={el => el.parentElement}
      >
        <StyledAvatarGroupContainer>
          <AvatarGroup>
            {contacts.length === 2 ? (
              contacts.map(contact => (
                <Avatar
                  key={contact.id}
                  // Match default size on Contact component
                  width={22}
                  // These avatars only show one initial
                  user={{ ...contact, lastName: undefined }}
                />
              ))
            ) : (
              <Avatar
                key="contact-count"
                // Match default width on Contact component
                width={22}
                // 3+ contacts just shows an orange count instead of any avatars
                user={avatarCountInitials}
                backgroundColor="orange2"
                color="orange6"
              />
            )}
          </AvatarGroup>
          <NamesListLabel>{namesList}</NamesListLabel>
        </StyledAvatarGroupContainer>
      </Dropdown>
    );
  }

  return (
    <div onClick={e => e.stopPropagation()}>
      {ContactsSection}
      <StyledAddressName ellipsis>{contacts[0]?.orgName}</StyledAddressName>
    </div>
  );
};

export default MultiContactEntry;

const StyledMenu = styled(Menu)`
  width: fit-content;
  min-width: 200px;
`;

export const StyledAddressName = styled(SecondaryDescriptionText)`
  && {
    display: block;
  }
  text-align: left;
  text-overflow: ellipsis;
`;

const StyledAvatarGroupContainer = styled.div`
  display: flex;
  align-items: center;
`;

const NamesListLabel = styled(Text)`
  margin-left: 8px;
`;
